/**
 * Google Gemini Direct Provider
 */

import { DirectProvider } from '../base/DirectProvider'
import type { AIResponseWithReason, ContinueContext } from '../../types'
import { getContinuePrompt } from '../../writing-utils/writing-prompts'

export class GoogleProvider extends DirectProvider {
  name = 'Google Gemini'
  
  constructor(apiKey: string, model = 'gemini-pro') {
    // Handle model ID format (remove 'models/' prefix if already present)
    const modelId = model.startsWith('models/') 
      ? model.substring(7) // Remove 'models/' prefix
      : model
    
    super({
      apiKey,
      model,
      apiUrl: `https://generativelanguage.googleapis.com/v1beta/models/${modelId}:generateContent?key=${apiKey}`,
      maxTokens: 8192  // Gemini 1.5 Flash/Pro support 8K output tokens (despite 1M+ input context)
    })
  }
  
  protected buildHeaders(): HeadersInit {
    return {
      'Content-Type': 'application/json'
    }
  }
  
  protected buildRequestBody(systemPrompt: string, userPrompt: string): any {
    return {
      contents: [{
        parts: [{
          text: `${systemPrompt}\n\n${userPrompt}`
        }]
      }],
      generationConfig: {
        temperature: this.temperature,
        maxOutputTokens: this.maxTokens
      }
    }
  }
  
  protected extractTextFromResponse(data: any): string {
    // Handle various response formats from Google API
    if (!data) {
      throw new Error('Empty response from Google API')
    }
    
    // Check if candidates exist and have content
    if (!data.candidates || !Array.isArray(data.candidates) || data.candidates.length === 0) {
      // Check for error message in response
      if (data.error) {
        throw new Error(`Google API error: ${data.error.message || JSON.stringify(data.error)}`)
      }
      throw new Error('No candidates in Google API response')
    }
    
    const candidate = data.candidates[0]
    
    // Check finish reason first
    if (candidate.finishReason === 'MAX_TOKENS') {
      throw new Error('Response truncated: Maximum token limit reached. Try a shorter prompt or request.')
    }
    if (candidate.finishReason === 'SAFETY') {
      throw new Error('Content blocked by Google safety filters')
    }
    
    // Check for content
    if (!candidate.content || !candidate.content.parts || candidate.content.parts.length === 0) {
      // If there's no content but a specific finish reason, report it
      if (candidate.finishReason) {
        throw new Error(`Generation stopped: ${candidate.finishReason}`)
      }
      throw new Error('No content in Google API response')
    }
    
    return candidate.content.parts[0].text || ''
  }
  
  /**
   * Google API URL은 API 키를 포함하므로 callAPI 오버라이드
   */
  protected async callAPI(systemPrompt: string, userPrompt: string): Promise<string> {
    // Handle model ID format (remove 'models/' prefix if already present)
    const modelId = this.model.startsWith('models/') 
      ? this.model.substring(7) // Remove 'models/' prefix
      : this.model
    
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${modelId}:generateContent?key=${this.apiKey}`
    
    const response = await fetch(url, {
      method: 'POST',
      headers: this.buildHeaders(),
      body: JSON.stringify(this.buildRequestBody(systemPrompt, userPrompt))
    })
    
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`${this.name} API error: ${response.status} - ${errorText}`)
    }
    
    const data = await response.json()
    return this.extractTextFromResponse(data)
  }

  /**
   * Continue writing with streaming support
   */
  async continueStreaming(
    text: string, 
    context?: ContinueContext,
    onChunk?: (chunk: string) => void
  ): Promise<string | AIResponseWithReason> {
    const mode = context?.mode || 'sentence'
    const prompts = getContinuePrompt(mode, context)
    
    // Modify system prompt for streaming - request plain text only
    const streamingSystemPrompt = prompts.system.replace(
      /응답 형식:[\s\S]*$/m,
      '이어쓴 텍스트만 반환하세요. JSON 형식이나 추가 설명 없이 순수한 텍스트만 반환하세요.'
    )
    
    // Handle model ID format (remove 'models/' prefix if already present)
    const modelId = this.model.startsWith('models/') 
      ? this.model.substring(7)
      : this.model
    
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${modelId}:streamGenerateContent?key=${this.apiKey}`
    
    const requestBody = {
      contents: [{
        parts: [{
          text: `${streamingSystemPrompt}\n\n${prompts.user(text)}`
        }]
      }],
      generationConfig: {
        temperature: this.temperature,
        maxOutputTokens: this.maxTokens
      }
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: this.buildHeaders(),
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Google API error: ${response.status} - ${errorText}`)
    }

    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('Failed to get response reader')
    }

    const decoder = new TextDecoder()
    let fullText = ''
    let buffer = ''

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        
        // Google uses JSON streaming with array of objects
        const lines = buffer.split('\n')
        buffer = lines[lines.length - 1] // Keep incomplete line in buffer
        
        for (let i = 0; i < lines.length - 1; i++) {
          const line = lines[i].trim()
          if (!line || line === '[' || line === ']') continue
          
          // Remove trailing comma if present
          const jsonStr = line.endsWith(',') ? line.slice(0, -1) : line
          
          try {
            const parsed = JSON.parse(jsonStr)
            
            if (parsed.candidates && parsed.candidates[0]) {
              const candidate = parsed.candidates[0]
              
              if (candidate.finishReason === 'STOP') {
                break
              }
              
              if (candidate.content?.parts?.[0]?.text) {
                const content = candidate.content.parts[0].text
                fullText += content
                onChunk?.(content)
              }
            }
          } catch (e) {
            // Skip invalid JSON
          }
        }
      }
    } finally {
      reader.releaseLock()
    }

    return {
      text: fullText,
      reason: undefined  // Continue is generation, not modification
    }
  }
  
  /**
   * Process custom prompt with streaming support
   */
  async processCustomPromptStreaming(
    text: string, 
    userPrompt: string, 
    mode?: 'sentence' | 'paragraph',
    onChunk?: (chunk: string) => void
  ): Promise<AIResponseWithReason> {
    // Check if this is a generation request (empty text) or modification request
    const isGeneration = !text || text.trim() === ''
    
    let systemPrompt: string
    let fullUserPrompt: string
    
    if (isGeneration) {
      // Text generation mode
      systemPrompt = `한국어 텍스트를 생성하는 전문 작가입니다.`
      
      if (mode === 'sentence') {
        systemPrompt += ' 한 문장으로 간결하게 작성하세요.'
      } else if (mode === 'paragraph') {
        systemPrompt += ' 3-5 문장으로 구성된 한 문단으로 작성하세요.'
      }
      
      systemPrompt += ' 생성된 텍스트만 반환하세요. JSON 형식이나 추가 설명 없이 순수한 텍스트만 반환하세요.'
      fullUserPrompt = `요청사항: ${userPrompt}`
    } else {
      // Text modification mode
      systemPrompt = `다음 텍스트를 사용자의 요청에 따라 수정해주세요.`
      
      if (mode === 'sentence') {
        systemPrompt += ' 한 문장으로 간결하게 작성하세요.'
      } else if (mode === 'paragraph') {
        systemPrompt += ' 한 문단으로 자세하게 작성하세요.'
      }
      
      systemPrompt += ' 수정된 텍스트만 반환하세요. JSON 형식이나 추가 설명 없이 순수한 텍스트만 반환하세요.'
      fullUserPrompt = `원문: ${text}\n\n요청사항: ${userPrompt}`
    }
    
    const requestBody = {
      contents: [{
        role: 'user',
        parts: [{
          text: fullUserPrompt
        }]
      }],
      systemInstruction: {
        parts: [{
          text: systemPrompt
        }]
      },
      generationConfig: {
        temperature: this.temperature,
        maxOutputTokens: this.maxTokens,
        candidateCount: 1
      }
    }
    
    const response = await fetch(`${this.apiUrl}?alt=sse&key=${this.apiKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Google API error: ${response.status} - ${errorText}`)
    }

    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('Failed to get response reader')
    }

    const decoder = new TextDecoder()
    let fullText = ''

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value)
        const lines = chunk.split('\n').filter(line => line.trim() !== '')

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            
            try {
              const parsed = JSON.parse(data)
              
              // Google uses candidates array for content
              const content = parsed.candidates?.[0]?.content?.parts?.[0]?.text
              if (content) {
                // Google may send full text each time, so we need to extract new chunk
                const newContent = content.substring(fullText.length)
                if (newContent) {
                  fullText += newContent
                  onChunk?.(newContent)
                }
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } finally {
      reader.releaseLock()
    }

    // Only provide reason for modifications, not for generations
    if (isGeneration) {
      return {
        text: fullText,
        reason: undefined
      }
    }

    return {
      text: fullText,
      reason: `프롬프트: "${userPrompt}"`
    }
  }
}